/**
 * The chosen file, in the rail beside the settings — for an importer whose input is already
 * an image.
 *
 * pdf-to-figma shows a column of page thumbnails and photoshop shows the composite it drew.
 * An importer that takes a PNG, a screenshot or a JPEG has no rendering to do at all: the
 * file IS the preview, and a browser can draw it. That is the whole component, and it is
 * here rather than copied into each of them because three apps wanted the same six lines.
 *
 * It does not report dimensions or anything else about the file. The rail answers one
 * question — "is this the thing I meant to pick?" — and a name in the file bar answers it
 * less well than the picture does.
 */

import { Box, Text } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';

export interface FilePreviewProps {
  /** The chosen file. Null renders nothing rather than an empty frame. */
  file: File | null;
  /** Overrides the default "can't draw this" line — e.g. for a format browsers refuse. */
  fallback?: string;
}

export function FilePreview({ file, fallback }: FilePreviewProps) {
  /*
   * A memo to create and an effect only to revoke. The URL is a pure function of the file,
   * so creating it in an effect would render once without an image and again with it — and
   * without the revoke, every file picked leaks a blob for the life of the window.
   */
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  /*
   * Decoding can fail — a RAW file, a TIFF, anything the browser will not draw — and a
   * broken-image glyph in the rail reads as a broken importer. `naturalWidth` is 0 in that
   * case and onError is the signal; photoshop learned this with a PSD thumbnail that
   * decoded nowhere.
   */
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);

  if (!url) return null;

  if (failed) {
    return (
      <Box
        w="full"
        py="6"
        px="2"
        borderRadius="sm"
        borderWidth="1px"
        borderColor="figma.border"
        bg="figma.panel"
        textAlign="center"
      >
        <Text fontSize="9px" color="figma.textMuted" lineHeight="1.4">
          {fallback ?? 'No preview for this format'}
        </Text>
      </Box>
    );
  }

  return (
    <img
      src={url}
      alt=""
      onError={() => setFailed(true)}
      style={{
        width: '100%',
        display: 'block',
        borderRadius: '2px',
        border: '1px solid var(--color-figma-border)',
        background: '#fff',
      }}
    />
  );
}
